/**
 * Tiered rendering detection (spec Page 8.4, mandatory).
 *
 * High   — full particle count for "The Ascent" (1200 particles).
 * Medium — reduced particle count (700, or 360 on mobile).
 * Low / no WebGL / prefers-reduced-motion — no <canvas> at all. The caller
 * must render the static CSS fallback instead (see StaticFallback in
 * AscentCanvas.tsx). Build the fallback first, then layer 3D on top.
 */
export type Tier = 'high' | 'medium' | 'low';

export interface Capability {
  tier: Tier;
  reducedMotion: boolean;
  webgl: boolean;
  isMobile: boolean;
}

function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function probeFps(sampleMs = 500): Promise<number> {
  return new Promise((resolve) => {
    let frames = 0;
    let start = 0;
    let settled = false;
    function tick(now: number) {
      if (settled) return;
      if (!start) start = now;
      frames++;
      if (now - start < sampleMs) {
        requestAnimationFrame(tick);
      } else {
        settled = true;
        resolve((frames * 1000) / (now - start));
      }
    }
    requestAnimationFrame(tick);
    // A backgrounded/throttled tab can suspend rAF entirely, which would
    // otherwise leave this promise — and detectCapability() with it —
    // pending forever, meaning neither the 3D Earth nor its static
    // fallback ever renders. Assume a capable device rather than hang.
    setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(60);
    }, sampleMs + 500);
  });
}

export async function detectCapability(): Promise<Capability> {
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const webgl = typeof document !== 'undefined' && hasWebGL();

  if (reducedMotion || !webgl) {
    return { tier: 'low', reducedMotion, webgl, isMobile };
  }

  const cores = (navigator as Navigator).hardwareConcurrency ?? 4;
  // deviceMemory is a non-standard Chrome-only API; not in lib.dom types.
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

  if (cores <= 2 || mem <= 2) {
    return { tier: 'low', reducedMotion, webgl, isMobile };
  }

  const fps = await probeFps();

  if (fps < 30) {
    return { tier: 'low', reducedMotion, webgl, isMobile };
  }
  if (cores <= 4 || mem <= 4 || fps < 50 || isMobile) {
    return { tier: 'medium', reducedMotion, webgl, isMobile };
  }
  return { tier: 'high', reducedMotion, webgl, isMobile };
}
